import { useEffect, useMemo } from 'react';
import { RuntimeFilterOp } from '@thoughtspot/visual-embed-sdk/react';
import { getInfinityListPropsFromQuery, MultiSelectWithQueriesUrl } from 'shared/ui/MultiSelect';
import { ContractorCampaignsPageFilter, useGetCampaignsInfinityList } from 'entities/campaign';
import { FILTER_STYLES } from 'shared/constants';
import { useGetServicesForFilters } from 'entities/service';
import { ContractorReportsFilter } from 'entities/reports';
import { useUrlResolver } from 'shared/lib/url';

import {
  getCampaignsOptionsNameIdFromInfinite,
  getJanFirstThisYear,
  useGetQueryParams,
  useGetServicesOptionsAliasId,
} from '../utils';
import { LiveboardEmbedConstructor } from '../LiveboardEmbedConstructor';

import { getDateOptions } from './utils';

const LIVEBOARD_ID = '7d2daf20-a438-4140-b52d-06c7a58fb845';
const selectedServicesQueryKey = ContractorReportsFilter.serviceIdArray;
const selectedCampaignsQueryKey = ContractorReportsFilter.campaignsIdArray;
const dateFromQueryKey = ContractorReportsFilter.from;

export function OverviewReport() {
  const servicesOptions = useGetServicesOptionsAliasId();
  const servicesFiltersQuery = useGetServicesForFilters();

  const selectedServices = useGetQueryParams(selectedServicesQueryKey);
  const selectedCampaigns = useGetQueryParams(selectedCampaignsQueryKey);

  const dateFrom = useGetQueryParams(dateFromQueryKey);
  const dateOptions = getDateOptions();

  const { addParam } = useUrlResolver();

  useEffect(() => {
    if (!dateFrom.length) {
      addParam(dateFromQueryKey, getJanFirstThisYear());
    }
  }, []);

  const getCampaignsFiltersQuery = useGetCampaignsInfinityList({
    isSyncedWithUrl: false,
    filterName: ContractorCampaignsPageFilter.campaignsIdArray,
    getFieldValueToShow: (item) => item.name,
  });

  const campaignsOptions = useMemo(() => {
    return getCampaignsOptionsNameIdFromInfinite(getCampaignsFiltersQuery.data);
  }, [getCampaignsFiltersQuery.data?.pages]);

  const getRuntimeFilters = () => {
    const runtimeFilters = [];

    if (selectedServices.length && selectedServices.length < servicesOptions.length) {
      runtimeFilters.push({
        columnName: 'Service Alias',
        operator: RuntimeFilterOp.IN,
        values: selectedServices,
      });
    }

    if (selectedCampaigns.length && selectedCampaigns.length < campaignsOptions.length) {
      runtimeFilters.push({
        columnName: 'Campaign Name',
        operator: RuntimeFilterOp.IN,
        values: selectedCampaigns,
      });
    }

    if (dateFrom.length) {
      runtimeFilters.push({
        columnName: 'Period Start',
        operator: RuntimeFilterOp.GE,
        values: [dateFrom[0]],
      });
    }

    return runtimeFilters;
  };

  const runtimeFilters = getRuntimeFilters();

  if (!servicesOptions.length) {
    return null;
  }

  return (
    <LiveboardEmbedConstructor
      type="primary"
      filters={
        <>
          <MultiSelectWithQueriesUrl.Search
            rootClassName={`${FILTER_STYLES.select} min-w-[345px]`}
            title="Service"
            options={servicesOptions}
            disabled={servicesFiltersQuery.isLoading}
            loading={servicesFiltersQuery.isLoading}
            filterName={selectedServicesQueryKey}
            isWhite
          />
          <MultiSelectWithQueriesUrl.InfinityList
            {...getInfinityListPropsFromQuery(getCampaignsFiltersQuery)}
            rootClassName={`${FILTER_STYLES.select} min-w-[345px]`}
            title="Campaign name"
            key="Campaign"
            options={campaignsOptions}
            filterName={selectedCampaignsQueryKey}
            isWhite
          />
          <MultiSelectWithQueriesUrl.Select
            rootClassName={`${FILTER_STYLES.select} min-w-[345px]`}
            title="Datepicker"
            filterName={dateFromQueryKey}
            options={dateOptions}
            isWhite
            withCancel={false}
            leftIcon="calendar_month"
          />
        </>
      }
      title="Overview"
      liveBoardId={LIVEBOARD_ID}
      runtimeFilters={runtimeFilters}
    />
  );
}
